import { RoutesPath } from 'core/routes.types';
import { useCustomNavigate } from 'hooks/useCustomNavigate';
import { useGetOrderReceipt } from 'hooks/useGetOrderReceipt';
import { useOrderStatus } from 'hooks/useOrderStatus/useOrderStatus';
import { DateTime } from 'luxon';
import { computeProductsForCart } from 'pages/Cart/Cart.utils';
import { formatOrderDate } from 'pages/Order/utils';
import { useEffect } from 'react';
import { generatePath, useParams } from 'react-router-dom';
import { BrandRestaurant } from 'services/brand/brand.type';
import {
    CartProduct,
    Discount,
    EntranceFee,
    FollowUpStatus,
    Grant,
    OrderInformation,
    PriceBreakdown,
} from 'services/order/order.type';

export type OrderReceipt = {
    orderUuid: string;
    datetime: string;
    restaurant: BrandRestaurant;
    ticketNumber: string;
    products: CartProduct[];
    currency: string;
    priceBreakdown: PriceBreakdown;
    grants: Grant[];
    fees: EntranceFee[];
    discounts: Discount[];
};

export const useOrderReceiptVM = () => {
    const { orderId } = useParams();
    const { data, isLoading, error } = useGetOrderReceipt(orderId ?? '');
    const orderReceipt = data ? toOrderReceipt(data) : null;
    const { initializeOrderStatus, ordersStatus } = useOrderStatus();

    useEffect(() => {
        const isToday = DateTime.fromISO(data?.createdAt ?? '').hasSame(DateTime.now(), 'day');
        if (data && data.restaurantId && isToday) {
            initializeOrderStatus(
                data.omnichannelOrderId,
                data.restaurantId.toString(),
                data.mainStatus,
            );
        }
    }, [orderId, ordersStatus, initializeOrderStatus, data]);

    const orderStatus = ordersStatus?.[orderId ?? ''];

    const { navigateWithParams, handleGoBack } = useCustomNavigate();

    const handleClickStatusBanner = () => {
        if (orderReceipt) {
            navigateWithParams(
                `../${generatePath(RoutesPath.OrderFollowUp, {
                    orderUuid: orderReceipt?.orderUuid,
                })}`,
            );
        }
    };

    const status = orderStatus?.status as FollowUpStatus | undefined;

    return {
        orderReceipt,
        isLoading,
        error,
        orderStatus,
        handleClickStatusBanner,
        status,
        handleGoBack,
    };
};

function toOrderReceipt(information: OrderInformation): OrderReceipt {
    const rawData = JSON.parse(information.cartRawData);
    const products = computeProductsForCart(
        // If enrichedCart is undefined, it's a legacy order (webordering v1)
        rawData.enrichedCart ? rawData.enrichedCart : rawData.products,
        information.currency,
    );

    return {
        datetime: formatOrderDate(information.startDate),
        restaurant: information.restaurant,
        ticketNumber: information.ticketNumber ?? '',
        products,
        currency: information.currency,
        priceBreakdown: computePriceBreakdown(information),
        grants: information.grants,
        fees: information.entranceFees,
        discounts: information.discounts,
        orderUuid: information.orderUuid,
    };
}

function computePriceBreakdown(information: OrderInformation) {
    const priceBreakdown = {
        totalExclTax: information.totalPriceWithTaxExcluded,
        totalInclTaxWithDiscount: information.totalPriceDiscountedWithTaxIncluded,
        totalInclTax: information.totalPriceWithTaxIncluded,
        totalExclTaxWithDiscount: information.totalPriceWithTaxExcluded,
        totalTax: information.totalPriceWithTaxIncluded - information.totalPriceWithTaxExcluded,
        totalTaxWithDiscount:
            information.totalPriceDiscountedWithTaxIncluded - information.totalPriceWithTaxExcluded,
        totalInclTaxWithRemise: information.totalPriceWithTaxIncluded,
        totalExclTaxWithRemise: information.totalPriceWithTaxExcluded,
        totalTaxWithRemise:
            information.totalPriceWithTaxIncluded - information.totalPriceWithTaxExcluded,
    };

    return priceBreakdown;
}
